import {DEMO_REQUEST_PATH} from '@/components/www/demo-request/demoPage'
import {LISTING_REJECTIONS_PATH} from '@/components/www/listing-rejections/listingRejectionsLp'
import type {LandingFaqItem} from '@/components/www/landing/landingFaq'
import {MONTHLY_GMV_OPTIONS} from '@/components/www/forms/monthlyGmvOptions'

export const NORDSTROM_LP_PATH = '/sell-on-nordstrom'

/**
 * Fallbacks for the Nordstrom taxonomy counts in the rejections section, which
 * reads live values from the same API that powers /marketplaces/nordstrom
 * (`useTaxonomyQuery('nordstrom')`). These apply only before that request
 * resolves or where the taxonomy isn't synced (local dev returns 0). Values
 * verified against production /api/www/taxonomies/nordstrom on Aug 28, 2026.
 */
export const NORDSTROM_TAXONOMY_FALLBACK_COUNTS = {
    categories: 526,
    fields: 241,
} as const

/**
 * Real Nordstrom required attributes for clothing, used by the rejections reel.
 *
 * Source: the vendored Mirakl hierarchy-attributes response at
 * `spec/fixtures/channels/mirakl/api/list_hierarchy_attributes/npt.clothing.json`
 * — every `attributes[].label` whose `required` is true, deduplicated in the
 * order Nordstrom returns them. Re-derive from that fixture if the spec moves;
 * do not hand-edit or invent entries. These names are decorative, so they are
 * baked in rather than fetched — the section's category/field *counts* are the
 * factual claim and those come from the live taxonomy API.
 */
export const NORDSTROM_REQUIRED_FIELD_NAMES = [
    'Nordstrom Product Taxonomy (NPT)',
    'Shop SKU',
    'Product Title',
    'UPC',
    'Variant Group Code',
    'Brand Label Name',
    'Age',
    'Gender',
    'Color Description',
    'Color Family',
    'NRF Color Code',
    'Copy Description',
    'Copy Details and Care (Features)',
    'Country of Manufacture - Primary',
    'Product Image 1 URL (Main)',
    'Material 1',
    'Material 1 Composition Percentage',
    'Size Standard',
    'Size Type',
    'Washing Instructions',
    'Size (Primary): Clothing',
    'Sleeve Length',
    'Size (Secondary): Suit Length',
    'Size (Secondary): Inseam',
    'Size (Secondary): Sleeve Length',
    'Size (Primary): Bras',
    'Size (Secondary): Bra Cup',
] as const

/** Where the reel settles. Same source and rules as the pool above. */
export const NORDSTROM_REEL_FINAL_FIELDS = [
    'Size (Secondary): Inseam',
    'Sleeve Length',
    'Material 1 Composition Percentage',
    'NRF Color Code',
    'Country of Manufacture - Primary',
    'Washing Instructions',
] as const

export function nordstromDemoPath(cta: string): string {
    return `${DEMO_REQUEST_PATH}?source=nordstrom-lp&cta=${encodeURIComponent(cta)}`
}

export interface NordstromStatPill {
    id: string
    value: string
    label: string
}

export interface NordstromDesireCard {
    id: string
    title: string
    body: string
}

export interface NordstromDoneItem {
    id: string
    num: string
    title: string
    body: string
}

export interface NordstromRejectionPoint {
    id: string
    /** `{categories}` and `{fields}` are replaced with the live taxonomy counts. */
    text: string
}

export interface NordstromHowStep {
    id: string
    step: string
    title: string
    body: string
}

export interface NordstromBrandBookItem {
    id: string
    text: string
}

export interface NordstromAboutPillar {
    id: string
    title: string
    body: string
}

/** Shared with /listing-rejections — both pages render LandingFaqAccordion. */
export type NordstromFaqItem = LandingFaqItem

export interface NordstromQuizQuestion {
    id: string
    prompt: string
    options: Array<{value: string; label: string}>
}

export const NORDSTROM_LP = {
    hero: {
        titleLine1: 'Your brand,',
        titleLine2: 'live on Nordstrom.',
        subhead:
            'We handle the entire application. The brand book, the paperwork, the listings. You take one call and connect Shopify.',
        ctaNote: 'A 15 minute call. No commitment, no pitch deck.',
        ctaSupport: 'Universal Standard and Furniq UK sell on Nordstrom through Osello.',
    },
    statsBar: {
        items: [
            {id: 'furniq-time', value: '3 weeks', label: 'Furniq UK · live on Nordstrom'},
            {id: 'approval', value: '93%', label: 'Universal Standard · listings approved first pass'},
            {id: 'skus', value: '5,000+', label: 'Universal Standard · SKUs syncing daily'},
        ] satisfies NordstromStatPill[],
    },
    desire: {
        statement: 'See your name on nordstrom.com.',
        support:
            'On nordstrom.com, your label sits next to the ones your customers already trust. Buyers, press, and every future retail partner read that placement.',
        cards: [
            {
                id: 'credibility',
                title: 'Credibility',
                body: '"Available at Nordstrom" does the selling for you. With customers, with press, with every retail conversation that follows.',
            },
            {
                id: 'customers',
                title: 'Full price',
                body: 'Nordstrom shoppers come for quality, not markdowns. Your margins stay where you set them.',
            },
            {
                id: 'control',
                title: 'No wholesale risk',
                body: 'No purchase orders, no consignment, no net-60. You ship from your own inventory and keep control.',
            },
        ] satisfies NordstromDesireCard[],
    },
    doneForYou: {
        title: 'Worried about getting approved? We handle everything.',
        subhead:
            'Most brands apply cold, wait months, and never hear back. Getting accepted is a real project. Brand presentation, application, catalog specs, imagery requirements. It\'s our whole job, so it never becomes yours.',
        items: [
            {
                id: 'brand-book',
                num: '01',
                title: 'Your brand book',
                body: 'The presentation Nordstrom\'s team reviews. Story, positioning, target customer, visual identity. Built from your website, by us.',
            },
            {
                id: 'application',
                num: '02',
                title: 'Your application',
                body: 'Prepared, submitted, and managed through every round of back and forth until you\'re approved.',
            },
            {
                id: 'listings',
                num: '03',
                title: 'Your listings',
                body: 'Your Shopify catalog mapped to Nordstrom\'s spec. Categories, size charts, imagery. Nothing gets rejected twice.',
            },
            {
                id: 'sync',
                num: '04',
                title: 'Your operations',
                body: 'Once you\'re live, inventory, prices, and orders flow between Shopify and Nordstrom on their own.',
            },
        ] satisfies NordstromDoneItem[],
        closer: 'You approve. You ship. That\'s it.',
    },
    rejections: {
        title: 'What actually gets your listing rejected?',
        intro: 'Not your product. A field.',
        points: [
            {
                id: 'field-names',
                text: 'Nordstrom wants inseam. Macy\'s wants sleeve length. Target Plus wants both, under different names.',
            },
            {
                id: 'silent-failure',
                text: 'Miss one and the listing never goes live, and the rejection notice will not tell you which field it was.',
            },
            {
                id: 'coverage',
                text: 'Nordstrom publishes {categories} categories and {fields} listing fields. We map your catalog to every one of them before anything is submitted.',
            },
        ] satisfies NordstromRejectionPoint[],
        cta: 'See if your brand qualifies',
    },
    howItWorks: {
        title: 'Three steps to Nordstrom.',
        steps: [
            {
                id: 'call',
                step: '01',
                title: 'Take the call',
                body: 'Fifteen minutes with our team. We will confirm if your brand might fit and tell you exactly what happens next.',
            },
            {
                id: 'connect',
                step: '02',
                title: 'Connect Shopify',
                body: 'One click. Products, variants, images, inventory. No exports, no spreadsheets.',
            },
            {
                id: 'apply',
                step: '03',
                title: 'Go live',
                body: 'We build the brand book, submit the application, set up the listings. Then your brand is on nordstrom.com.',
            },
        ] satisfies NordstromHowStep[],
    },
    brandBook: {
        title: 'Get the brand book Nordstrom wants to see.',
        subhead:
            'Drop in your website. We build your brand book and email it to you, free. It\'s the same document that anchors a Nordstrom application.',
        items: [
            {id: 'story', text: 'Brand story'},
            {id: 'positioning', text: 'Positioning'},
            {id: 'customer', text: 'Target customer'},
            {id: 'merchandising', text: 'Merchandising strategy'},
            {id: 'identity', text: 'Visual identity'},
        ] satisfies NordstromBrandBookItem[],
        cta: 'Get my free brand book',
        finePrint: 'Free. Built from your website. In your inbox within 48 hours.',
    },
    about: {
        title: 'So, who is Osello?',
        lede:
            'Osello is the marketplace platform built for fashion brands. One connection to Shopify puts your catalog on the retailers that matter and keeps it in sync every day, with a team behind it that has done this before. Nordstrom is where we do our best work.',
        pillars: [
            {
                id: 'platform',
                title: 'Software does the heavy lifting',
                body:
                    'Products, imagery, inventory, and orders sync between Shopify and Nordstrom daily. One brand runs 5,000+ SKUs through it without a single spreadsheet. Our team handles everything the software can\'t.',
            },
            {
                id: 'fashion',
                title: 'Fashion is the specialty',
                body:
                    'Size runs, colorways, seasonal drops, imagery standards. We speak retail fashion fluently, which is why brands like Universal Standard and Furniq UK trust us with their catalogs.',
            },
            {
                id: 'growth',
                title: 'Nordstrom first, then everywhere',
                body:
                    'The catalog we build for Nordstrom is ready for Macy\'s, Bloomingdale\'s, and Target+ the day you want them. Get in once, grow everywhere.',
            },
        ] satisfies NordstromAboutPillar[],
        logosLabel: 'Where Osello puts brands',
        cta: 'Meet us on the intro call',
    },
    faq: {
        title: 'Questions, answered.',
        items: [
            {
                id: 'wholesale',
                question: 'Is this the same as selling wholesale to Nordstrom?',
                answer:
                    'No. This is Nordstrom Marketplace. You sell on nordstrom.com under your own brand, at full price, from your own inventory. No purchase orders, no consignment, no waiting for a buyer to pick up your line.',
            },
            {
                id: 'cost',
                question: 'What does it cost?',
                answer:
                    'The intro call is free. If we take your brand on, we walk through pricing on that call. You\'ll know every number before you commit to anything.',
            },
            {
                id: 'us-presence',
                question: 'We\'re not a US company. Can we still get on?',
                answer:
                    'Nordstrom requires a US entity or US fulfillment. If you ship from a US warehouse or 3PL, you can qualify. Furniq UK is a British brand and went live in three weeks.',
            },
            {
                id: 'orders',
                question: 'Who handles orders, shipping, and returns?',
                answer:
                    'You fulfill the way you already do. Nordstrom orders land in your Shopify like any other order, and we keep inventory and order status in sync on both sides so nothing oversells.',
            },
            {
                id: 'timeline',
                question: 'How long until my brand is live?',
                answer:
                    'Every catalog is different, so we won\'t promise a date before we\'ve seen yours. For reference, Furniq UK went live in three weeks, and Universal Standard\'s first listing was live five days after connecting.',
            },
            {
                id: 'rejection',
                question: 'What if Nordstrom says no?',
                answer:
                    'We only submit brands we believe will be approved. That\'s what the intro call is for. If something needs fixing first, we tell you exactly what it is and how to get there.',
            },
            {
                id: 'photography',
                question: 'Will my photography pass Nordstrom\'s standards?',
                answer:
                    'Your existing ecommerce imagery is the starting point. On the call we flag anything Nordstrom requires that you don\'t have, before it ever becomes a rejection.',
            },
            {
                id: 'channels',
                question: 'Do I have to change how I sell today?',
                answer:
                    'No. Nordstrom is additive. Your Shopify store, your site, your other channels all keep running exactly as they do now.',
            },
            {
                id: 'existing-provider',
                question: 'We already sell on Nordstrom. Can you take over from our current provider?',
                answer: 'Yes, and that is a different conversation from this one.',
                link: {label: 'Start here instead', to: LISTING_REJECTIONS_PATH},
            },
        ] satisfies NordstromFaqItem[],
    },
    finalCta: {
        title: 'Nordstrom is one call away.',
        subhead:
            'Fifteen minutes to see if your brand qualifies. We handle everything after that.',
        costNote: 'The call is free. You\'ll know every number before you commit to anything.',
    },
    gate: {
        title: 'Get your free brand book',
        lede:
            'Tell us where to look and where to send it. We build your brand book from your website and email it to you within 48 hours. Story, positioning, target customer, merchandising, visual identity.',
        submit: 'Build my brand book',
        trust: 'Free. No sales call required.',
        escape: 'Ready to talk instead?',
        successTitle: 'Your brand book is on the way.',
        successCopy: 'We\'re building it from your website now. It lands in your inbox within 48 hours. Want to see if your brand qualifies for Nordstrom in the meantime?',
    },
    quizExit: {
        title: 'Before you go, want your result by email?',
        // Shown instead of `title` when the visitor asked for the email from their result.
        resultTitle: 'Where should we send your result?',
        body: 'Leave your email and we\'ll review your answers and tell you exactly where your brand stands with Nordstrom. No call required.',
        submit: 'Email me my result',
        dismiss: 'No thanks',
        successTitle: 'Done. Watch your inbox.',
        successCopy: 'A real person will look at your answers and reply, usually within one business day.',
    },
} as const

export const NORDSTROM_QUIZ_QUESTIONS: NordstromQuizQuestion[] = [
    {
        id: 'fashion',
        prompt: 'Is your brand fashion or apparel?',
        options: [
            {value: 'yes', label: 'Yes, clothing, accessories, or footwear'},
            {value: 'no', label: 'No, other categories'},
        ],
    },
    {
        id: 'shopify',
        prompt: 'Are you selling on Shopify?',
        options: [
            {value: 'yes', label: 'Yes, Shopify or Shopify Plus'},
            {value: 'no', label: 'Not yet'},
        ],
    },
    {
        id: 'usa',
        prompt: 'Do you have a USA presence?',
        options: [
            {value: 'yes', label: 'Yes, US entity or US fulfillment'},
            {value: 'no', label: 'Not yet'},
        ],
    },
    {
        // Answer values are MarketingContact monthly_gmv_range keys, sent as-is on the exit save.
        id: 'monthly_gmv_range',
        prompt: 'What\'s your approximate monthly GMV across all channels?',
        options: MONTHLY_GMV_OPTIONS,
    },
]
